/**
 * LeadDossierCard — unified "Reunión comercial" card.
 *
 * Owns the commercial meeting + dossier sub-flow for a lead. Previous
 * inline "Reunión" section in LeadDetailPage / LeadSidePanel is folded in
 * here so there is a single source of truth for the meeting timestamp.
 *
 * Canonical timestamp: `meeting_at` (also persisted to scheduled_call_at
 * historically; the backend's read-side alias keeps legacy docs visible).
 * meeting_state drives calendar_service, alerts, automations — unchanged.
 *
 * Fields read/written on the lead doc:
 *   meeting_at, meeting_state,
 *   dossier_send_due_at, dossier_sent_at, dossier_sent_by, dossier_status
 *
 * Writes via PATCH /leads/{id}; POST /leads/{id}/dossier/mark-sent for the
 * dossier "marcar enviado" action. No background jobs, no automations
 * touched, no DB migration.
 */
import React, { useState } from "react";
import { api, formatError } from "@/lib/api";
import { CheckCircle, Clock, EnvelopeSimple, Calendar, X, CaretDown, CaretRight } from "@phosphor-icons/react";

const STATUS_TONE = {
  pending:      "bg-amber-50 text-amber-800 border-amber-200",
  sent:         "bg-emerald-50 text-emerald-700 border-emerald-200",
  not_required: "bg-stone-100 text-stone-600 border-stone-200",
};
const STATUS_LABEL = {
  pending:      "Pendiente",
  sent:         "Enviado",
  not_required: "No requerido",
};

const MEETING_STATE_OPTIONS = [
  { key: "scheduled", label: "Programada" },
  { key: "done",      label: "Realizada"  },
  { key: "cancelled", label: "Cancelada"  },
];

function fmtDateTimeInput(iso) {
  if (!iso) return "";
  return String(iso).slice(0, 16);
}
function fmtNice(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-ES", {
      day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function LeadDossierCard({ lead, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // Collapse the optional "schedule dossier send" row by default when empty
  // to keep the card focused on the meeting itself.
  const [showDossierDueAt, setShowDossierDueAt] = useState(
    Boolean(lead?.dossier_send_due_at)
  );

  const status = lead?.dossier_status || "pending";
  const meetingState = lead?.meeting_state || "";
  const hasMeeting = Boolean(lead?.meeting_at || lead?.meeting_state);

  const patch = async (payload) => {
    setBusy(true); setError("");
    try {
      await api.patch(`/leads/${lead.lead_id}`, payload);
      onChanged && onChanged();
    } catch (e) { setError(formatError(e)); }
    finally { setBusy(false); }
  };

  const markSent = async () => {
    setBusy(true); setError("");
    try {
      await api.post(`/leads/${lead.lead_id}/dossier/mark-sent`, {});
      onChanged && onChanged();
    } catch (e) { setError(formatError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="dv-card space-y-3" data-testid="lead-dossier-card">
      <div className="flex items-center gap-2">
        <span className="inline-flex w-6 h-6 rounded-md bg-violet-50 text-violet-600 border border-violet-200 items-center justify-center">
          <Calendar size={13} />
        </span>
        <h3 className="text-sm font-medium text-stone-800">Reunión comercial</h3>
        <span
          className={`ml-auto inline-flex text-[10px] px-2 py-0.5 rounded-full border ${STATUS_TONE[status]}`}
          title="Estado del dossier"
        >
          Dossier · {STATUS_LABEL[status]}
        </span>
      </div>

      {error && (
        <div className="text-xs px-2 py-1.5 rounded border bg-red-50 border-red-200 text-red-700" data-testid="lead-dossier-error">
          {error}
        </div>
      )}

      {/* Meeting fields — canonical source of truth */}
      <label className="block text-xs text-stone-500">
        Fecha y hora
        <div className="flex items-center gap-2 mt-1">
          <Calendar size={14} className="text-stone-400" />
          <input
            type="datetime-local"
            className="dv-input flex-1 text-sm"
            value={fmtDateTimeInput(lead.meeting_at)}
            onChange={(e) => patch({ meeting_at: e.target.value || null })}
            data-testid="lead-meeting-at"
            disabled={busy}
          />
        </div>
      </label>

      <div className="text-xs text-stone-500">
        Estado
        <div className="flex flex-wrap gap-1.5 mt-1">
          {MEETING_STATE_OPTIONS.map((opt) => {
            const active = meetingState === opt.key;
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => patch({ meeting_state: opt.key })}
                disabled={busy}
                data-testid={`lead-meeting-state-${opt.key}`}
                className={`text-xs px-2.5 py-1 rounded-full border transition ${
                  active
                    ? "border-stone-900 bg-stone-900 text-white"
                    : "border-stone-200 bg-white text-stone-700 hover:border-stone-900"
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Dossier sub-flow */}
      <div className="border-t border-stone-100 pt-3 space-y-3">
        <div>
          <button
            type="button"
            onClick={() => setShowDossierDueAt((v) => !v)}
            className="text-[11px] text-stone-500 hover:text-stone-800 inline-flex items-center gap-1"
            data-testid="lead-dossier-due-at-toggle"
            aria-expanded={showDossierDueAt}
          >
            {showDossierDueAt ? <CaretDown size={11} /> : <CaretRight size={11} />}
            Programar envío del dossier (opcional)
          </button>
          {showDossierDueAt && (
            <div className="flex items-center gap-2 mt-1">
              <Clock size={14} className="text-stone-400" />
              <input
                type="datetime-local"
                className="dv-input flex-1 text-sm"
                value={fmtDateTimeInput(lead.dossier_send_due_at)}
                onChange={(e) => patch({ dossier_send_due_at: e.target.value || null })}
                data-testid="lead-dossier-due-at"
                disabled={busy}
              />
            </div>
          )}
          {showDossierDueAt && (
            <div className="text-[11px] text-stone-400 mt-0.5">
              Sugerencia: unas horas antes de la reunión.
            </div>
          )}
        </div>

        <label className="block text-xs text-stone-500">
          Estado del dossier
          <select
            className="dv-input w-full mt-1 text-sm"
            value={status}
            onChange={(e) => patch({ dossier_status: e.target.value })}
            data-testid="lead-dossier-status"
            disabled={busy}
          >
            <option value="pending">Pendiente</option>
            <option value="sent">Enviado</option>
            <option value="not_required">No requerido</option>
          </select>
        </label>

        {lead.dossier_sent_at && (
          <div className="text-xs text-stone-500">
            <EnvelopeSimple size={12} className="inline mr-1 text-stone-400" />
            Enviado el {fmtNice(lead.dossier_sent_at)}
          </div>
        )}

        <button
          onClick={markSent}
          disabled={busy || status === "sent"}
          className="dv-btn dv-btn-primary w-full text-sm disabled:opacity-50"
          data-testid="lead-dossier-mark-sent"
        >
          <CheckCircle size={14} /> Marcar dossier enviado
        </button>
      </div>

      {/* Footer actions — meeting-level */}
      <div className="border-t border-stone-100 pt-3 flex justify-between items-center gap-2 flex-wrap">
        {hasMeeting ? (
          <button
            type="button"
            onClick={() => patch({ meeting_at: null, meeting_state: null })}
            disabled={busy}
            data-testid="lead-meeting-clear"
            className="text-xs text-stone-600 hover:text-stone-900 inline-flex items-center gap-1.5 border border-stone-200 hover:border-stone-400 rounded px-2.5 py-1.5"
          >
            <X size={12} /> Quitar reunión
          </button>
        ) : <span />}
        <button
          type="button"
          onClick={() => patch({ meeting_state: "done", status: "dossier" })}
          disabled={busy}
          data-testid="lead-meeting-done-and-dossier"
          className="text-xs text-stone-700 hover:text-stone-900 inline-flex items-center gap-1.5 border border-stone-200 hover:border-stone-900 rounded px-2.5 py-1.5"
        >
          <CheckCircle size={12} weight="bold" /> Reunión realizada → dossier
        </button>
      </div>
    </div>
  );
}
